import React, { useState, useEffect, useCallback } from "react";
import { FaChartBar, FaExclamationTriangle, FaCheckCircle, FaInfoCircle,
         FaLightbulb, FaArrowRight, FaChevronDown, FaChevronRight,
         FaRoad, FaThLarge, FaBox } from "react-icons/fa";
import {useTheme, getT, getBrandColors} from "./ThemeContext";
import PageHeaderIcon from "./components/PageHeaderIcon";
import API_BASE from "./apiConfig";
import { useDateTimeFormat } from "./utils/formatters";
import StatusBadge from "./components/StatusBadge";

const BASE = API_BASE;
const FONT = "'Segoe UI', system-ui, sans-serif";
const BLUR = "blur(18px)";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const TYPE_COLORS = ["#3b82f6","#10b981","#f59e0b","#7c3aed","#ef4444","#06b6d4"];

function KPITile({ label, value, sub, color, T }) {
  return (
    <div style={{ background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                  border: T.glassBdr, borderRadius: 16, boxShadow: T.glassSh,
                  padding: "20px 22px", flex: "1 1 160px", minWidth: 150 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.textLabel, fontFamily: FONT,
                    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 26, fontWeight: 900, color: color || T.text, fontFamily: FONT,
                    lineHeight: 1.1 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

// ── Utilization Bar ────────────────────────────────────────────────────────────
function UtilBar({ lane, color, max, T }) {
  const pct  = lane.available_hours ? ((lane.booked_hours / lane.available_hours) * 100).toFixed(1) : "0.0";
  const barW = max > 0 && lane.available_hours ? (lane.booked_hours / max) * 100 : 0;
  const pctNum = parseFloat(pct);
  const barColor = pctNum >= 70 ? "#10b981" : pctNum >= 30 ? "#3b82f6" : pctNum > 0 ? "#f59e0b" : "#6b728066";

  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <div style={{ width: 7, height: 7, borderRadius: 2, background: color, flexShrink: 0 }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: FONT }}>
            {lane.lane_name}
          </span>
        </div>
        <span style={{ fontSize: 12, fontWeight: 800, color: barColor, fontFamily: FONT }}>{pct}%</span>
      </div>
      <div style={{ height: 8, borderRadius: 4, background: `${color}18`, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${barW}%`, borderRadius: 4,
                      background: `linear-gradient(90deg,${barColor}bb,${barColor})`,
                      transition: "width 0.7s ease" }} />
      </div>
      <div style={{ fontSize: 10, color: T.textMuted, fontFamily: FONT, marginTop: 3 }}>
        {Number(lane.booked_hours).toFixed(1)}h booked / {Number(lane.available_hours).toFixed(1)}h available
        · {lane.booking_count} booking{lane.booking_count !== 1 ? "s" : ""}
      </div>
    </div>
  );
}

// ── Collapsible Group ──────────────────────────────────────────────────────────
function UtilGroup({ title, icon: Icon, accent, lanes, T, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!lanes?.length) return null;

  const max        = Math.max(...lanes.map(l => Number(l.available_hours) || 0), 1);
  const totalBooked = lanes.reduce((s, l) => s + Number(l.booked_hours), 0);
  const totalAvail  = lanes.reduce((s, l) => s + Number(l.available_hours), 0);
  const groupPct    = totalAvail > 0 ? ((totalBooked / totalAvail) * 100).toFixed(1) : "0.0";

  return (
    <div style={{ marginBottom: 8 }}>
      {/* Group header */}
      <button onClick={() => setOpen(o => !o)} style={{
        display: "flex", alignItems: "center", gap: 9, width: "100%",
        padding: "10px 14px", borderRadius: 10, cursor: "pointer",
        background: `${accent}10`, border: `1px solid ${accent}30`,
        backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
        marginBottom: open ? 10 : 0, transition: "margin 0.2s",
      }}>
        <Icon size={13} color={accent} />
        <span style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: FONT, flex: 1, textAlign: "left" }}>
          {title}
        </span>
        <span style={{ fontSize: 11, fontWeight: 700, color: accent, fontFamily: FONT,
                       background: `${accent}18`, borderRadius: 8, padding: "2px 8px",
                       border: `1px solid ${accent}30` }}>
          {groupPct}% avg · {lanes.length} lane{lanes.length !== 1 ? "s" : ""}
        </span>
        {open
          ? <FaChevronDown size={10} color={T.textMuted} />
          : <FaChevronRight size={10} color={T.textMuted} />}
      </button>

      {/* Lane bars */}
      {open && (
        <div style={{ paddingLeft: 14, paddingRight: 4 }}>
          {lanes.map((l, i) => (
            <UtilBar key={l.lane_id} lane={l} color={TYPE_COLORS[i % TYPE_COLORS.length]} max={max} T={T} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Utilization Chart (collapsible tree) ──────────────────────────────────────
function UtilizationChart({ lanes, T }) {
  if (!lanes?.length) return (
    <div style={{ padding: 32, textAlign: "center", color: T.textMuted, fontFamily: FONT, fontSize: 13 }}>
      No utilization data available
    </div>
  );

  const fullLanes = lanes.filter(l => l.lane_type === "full_lane");
  const cages     = lanes.filter(l => l.lane_type === "cage");
  const boxes     = lanes.filter(l => l.lane_type === "box");

  return (
    <div>
      <UtilGroup title="Full Lanes"    icon={FaRoad}    accent="#6366f1" lanes={fullLanes} T={T} defaultOpen={true}  />
      <UtilGroup title="Cages"         icon={FaThLarge} accent="#0ea5e9" lanes={cages}     T={T} defaultOpen={false} />
      <UtilGroup title="Box Bookings"  icon={FaBox}     accent="#10b981" lanes={boxes}     T={T} defaultOpen={false} />
    </div>
  );
}

// ── Revenue by Type Donut ──────────────────────────────────────────────────────
function RevenueDonut({ data, T }) {
  if (!data || data.length === 0) return (
    <div style={{ padding: 32, textAlign: "center", color: T.textMuted, fontFamily: FONT, fontSize: 13 }}>
      No revenue data available
    </div>
  );

  const total = data.reduce((s, d) => s + parseFloat(d.revenue || 0), 0);
  const SIZE  = 180;
  const R     = 70;
  const CX    = SIZE / 2;
  const CY    = SIZE / 2;
  const INNER = 42;

  let cumAngle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const frac  = total > 0 ? parseFloat(d.revenue) / total : 0;
    const angle = frac * 2 * Math.PI;
    const x1    = CX + R * Math.cos(cumAngle);
    const y1    = CY + R * Math.sin(cumAngle);
    cumAngle   += angle;
    const x2    = CX + R * Math.cos(cumAngle);
    const y2    = CY + R * Math.sin(cumAngle);
    const xi1   = CX + INNER * Math.cos(cumAngle - angle);
    const yi1   = CY + INNER * Math.sin(cumAngle - angle);
    const xi2   = CX + INNER * Math.cos(cumAngle);
    const yi2   = CY + INNER * Math.sin(cumAngle);
    const large = angle > Math.PI ? 1 : 0;
    return { d: `M ${xi1} ${yi1} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} L ${xi2} ${yi2} A ${INNER} ${INNER} 0 ${large} 0 ${xi1} ${yi1} Z`,
             color: TYPE_COLORS[i % TYPE_COLORS.length], name: d.type_name, revenue: d.revenue,
             pct: (frac * 100).toFixed(1) };
  });

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE}
           style={{ flexShrink: 0 }}>
        {slices.map((s, i) => (
          <path key={i} d={s.d} fill={s.color} opacity={0.85} />
        ))}
        <text x={CX} y={CY - 6} textAnchor="middle" fill={T.text}
              fontSize="13" fontWeight="800" fontFamily={FONT}>
          ${total >= 1000 ? `${(total / 1000).toFixed(1)}k` : total.toFixed(0)}
        </text>
        <text x={CX} y={CY + 10} textAnchor="middle" fill={T.textMuted}
              fontSize="9" fontFamily={FONT}>
          Total Revenue
        </text>
      </svg>

      <div style={{ flex: 1 }}>
        {slices.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8,
                                 marginBottom: 10, justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: s.color }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: T.text, fontFamily: FONT }}>
                {s.name}
              </span>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: s.color, fontFamily: FONT }}>
                ${parseFloat(s.revenue).toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>{s.pct}%</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Recent Bookings Mini-List ──────────────────────────────────────────────────
const STATUS_META = {
  pending_payment: { label: "Pending",    tone: "warning" },
  confirmed:       { label: "Confirmed",  tone: "success" },
  checked_in:      { label: "Checked In", tone: "info" },
  completed:       { label: "Completed",  tone: "info" },
  cancelled:       { label: "Cancelled",  tone: "neutral" },
};

function RecentList({ bookings, T }) {
  const { fmtDate } = useDateTimeFormat();
  if (!bookings?.length) return (
    <div style={{ padding: "20px 0", textAlign: "center", color: T.textMuted,
                  fontFamily: FONT, fontSize: 13 }}>No recent bookings</div>
  );
  return (
    <div>
      {bookings.map(b => {
        const meta = STATUS_META[b.status] || { label: b.status, tone: "neutral" };
        return (
          <div key={b.booking_id} style={{ display: "flex", alignItems: "center",
                                            padding: "10px 0", borderBottom: `1px solid ${T.divider}`,
                                            gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: FONT }}>
                {b.client_name}
              </div>
              <div style={{ fontSize: 11, color: T.textMuted, fontFamily: FONT }}>
                {b.booking_ref} · {b.lane_name} · {fmtDate(b.booking_date)}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.text, fontFamily: FONT }}>
                ${parseFloat(b.total_amount).toFixed(2)}
              </div>
              <StatusBadge label={meta.label} tone={meta.tone}
                style={{ fontSize: 10, borderRadius: 12, padding: "2px 8px", fontFamily: FONT }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── AI Findings Panel ─────────────────────────────────────────────────────────
const FINDING_STYLES = {
  warning: { bg: "rgba(245,158,11,0.10)", border: "rgba(245,158,11,0.30)", icon: FaExclamationTriangle, color: "#f59e0b" },
  success: { bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.30)", icon: FaCheckCircle,         color: "#10b981" },
  info:    { bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.30)", icon: FaInfoCircle,          color: "#3b82f6" },
};

function AIFindings({ findings, T }) {
  if (!findings?.length) return (
    <div style={{ padding: "28px 0", textAlign: "center", color: T.textMuted, fontFamily: FONT, fontSize: 13 }}>
      No insights available yet — add more bookings to generate findings.
    </div>
  );
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
      {findings.map((f, i) => {
        const S = FINDING_STYLES[f.type] || FINDING_STYLES.info;
        const Icon = S.icon;
        return (
          <div key={i} style={{ background: S.bg, border: `1px solid ${S.border}`, borderRadius: 14,
                                padding: "16px 18px", display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
              <Icon size={15} color={S.color} style={{ marginTop: 2, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: FONT, marginBottom: 5 }}>
                  {f.title}
                </div>
                <div style={{ fontSize: 12, color: T.textMuted, fontFamily: FONT, lineHeight: 1.55 }}>
                  {f.body}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 800, background: `${S.color}22`, color: S.color,
                             borderRadius: 8, padding: "3px 9px", fontFamily: FONT }}>
                {f.metric}
              </span>
              {f.action && (
                <span style={{ fontSize: 11, fontWeight: 700, color: S.color, fontFamily: FONT,
                               display: "flex", alignItems: "center", gap: 4 }}>
                  {f.action} <FaArrowRight size={9} />
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────────
export default function LaneBookingDashboard() {
  const { isDark } = useTheme();
  const T = getT(isDark);

  const now = new Date();
  const [month,      setMonth]      = useState(now.getMonth() + 1);
  const [year,       setYear]       = useState(now.getFullYear());
  const [facilities, setFacilities] = useState([]);
  const [selFac,     setSelFac]     = useState("");
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    fetch(`${BASE}/lb/facilities`).then(r => r.json())
      .then(d => setFacilities(d.facilities || []));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const q = new URLSearchParams({ month, year });
    if (selFac) q.set("facility_id", selFac);
    fetch(`${BASE}/lb/dashboard?${q}`).then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [month, year, selFac]);

  useEffect(() => { load(); }, [load]);

  const card = { background: T.glass, backdropFilter: BLUR, WebkitBackdropFilter: BLUR,
                 border: T.glassBdr, borderRadius: 16, boxShadow: T.glassSh };

  const kpis = data?.kpis || {};

  return (
    <div style={{ padding: "28px 32px", maxWidth: 1200, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <PageHeaderIcon icon={<FaChartBar size={20} color={getBrandColors().blueDark} />} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: T.text, fontFamily: FONT }}>Lane Analytics</div>
          <div style={{ fontSize: 13, color: T.textMuted, fontFamily: FONT, marginTop: 2 }}>
            Revenue, utilization and booking activity
          </div>
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <select value={selFac} onChange={e => setSelFac(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.inputBdr}`,
                     background: T.inputBg, color: T.inputText, fontSize: 13, fontFamily: FONT, outline: "none" }}>
            <option value="">All Facilities</option>
            {facilities.map(f => <option key={f.facility_id} value={f.facility_id}>{f.facility_name}</option>)}
          </select>
          <select value={month} onChange={e => setMonth(Number(e.target.value))}
            style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.inputBdr}`,
                     background: T.inputBg, color: T.inputText, fontSize: 13, fontFamily: FONT, outline: "none" }}>
            {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))}
            style={{ padding: "8px 12px", borderRadius: 8, border: `1px solid ${T.inputBdr}`,
                     background: T.inputBg, color: T.inputText, fontSize: 13, fontFamily: FONT, outline: "none" }}>
            {[now.getFullYear(), now.getFullYear() - 1].map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", color: T.textMuted, fontFamily: FONT, padding: 60 }}>Loading…</div>
      ) : (
        <>
          {/* KPI tiles */}
          <div style={{ display: "flex", gap: 14, marginBottom: 22, flexWrap: "wrap" }}>
            <KPITile label="Total Revenue" color="#ffd700"
              value={`$${parseFloat(kpis.total_revenue || 0).toFixed(2)}`}
              sub={`${kpis.total_bookings || 0} bookings`} T={T} />
            <KPITile label="Member Revenue" color="#10b981"
              value={`$${parseFloat(kpis.member_revenue || 0).toFixed(2)}`}
              sub={`${kpis.member_bookings || 0} member bookings`} T={T} />
            <KPITile label="Non-Member Revenue" color="#3b82f6"
              value={`$${parseFloat(kpis.non_member_revenue || 0).toFixed(2)}`}
              sub={`${kpis.non_member_bookings || 0} bookings`} T={T} />
            <KPITile label="Avg Utilization" color="#7c3aed"
              value={`${parseFloat(kpis.avg_utilization || 0).toFixed(1)}%`}
              sub="across all lanes" T={T} />
            <KPITile label="Academy Bookings" color="#f59e0b"
              value={kpis.academy_bookings || 0}
              sub="internal use" T={T} />
            <KPITile label="Total Refunded" color="#ef4444"
              value={`$${parseFloat(kpis.total_refunded || 0).toFixed(2)}`}
              sub={`${kpis.refund_count || 0} refund${kpis.refund_count !== 1 ? "s" : ""}`} T={T} />
          </div>

          {/* Charts row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 18 }}>
            {/* Utilization */}
            <div style={{ ...card, padding: "20px 24px" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT, marginBottom: 16 }}>
                Lane Utilization
              </div>
              <UtilizationChart lanes={data?.utilization} T={T} />
            </div>

            {/* Revenue by type */}
            <div style={{ ...card, padding: "20px 24px" }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT, marginBottom: 16 }}>
                Revenue by Booking Type
              </div>
              <RevenueDonut data={data?.revenue_by_type} T={T} />
            </div>
          </div>

          {/* AI Findings */}
          <div style={{ ...card, padding: "20px 24px", marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
              <FaLightbulb size={16} color="#ffd700" />
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT }}>
                AI Findings & Recommendations
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, background: "rgba(255,215,0,0.15)",
                             color: "#ffd700", borderRadius: 8, padding: "2px 8px", fontFamily: FONT,
                             border: "1px solid rgba(255,215,0,0.3)" }}>
                {data?.findings?.length || 0} insight{data?.findings?.length !== 1 ? "s" : ""}
              </span>
            </div>
            <AIFindings findings={data?.findings} T={T} />
          </div>

          {/* Recent bookings */}
          <div style={{ ...card, padding: "20px 24px" }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: FONT, marginBottom: 14 }}>
              Recent Bookings
            </div>
            <RecentList bookings={data?.recent_bookings} T={T} />
          </div>
        </>
      )}
    </div>
  );
}
